import { useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { ...toast, id };
    
    setToasts(prev => [...prev, newToast]);
    
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toast = useCallback((
    title: string,
    description?: string,
    type: ToastType = 'info',
    duration?: number
  ) => {
    return addToast({ title, description, type, duration });
  }, [addToast]);

  const success = useCallback((title: string, description?: string, duration?: number) => {
    return toast(title, description, 'success', duration);
  }, [toast]);

  const error = useCallback((title: string, description?: string, duration?: number) => {
    return toast(title, description, 'error', duration);
  }, [toast]);

  const warning = useCallback((title: string, description?: string, duration?: number) => {
    return toast(title, description, 'warning', duration);
  }, [toast]);

  const info = useCallback((title: string, description?: string, duration?: number) => {
    return toast(title, description, 'info', duration);
  }, [toast]);

  return {
    toasts,
    addToast,
    removeToast,
    toast,
    success,
    error,
    warning,
    info,
  };
};
